import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sparkles, ArrowRight, Brain, Activity, Clock, User,
} from 'lucide-react';
import { patientDetails, paiBuilderData, paiTimelines } from '@/data/mockCaseManagerData';

export default function CaseManagerPAIHub() {
  const navigate = useNavigate();
  const patients = paiTimelines
    .filter((p) => patientDetails[p.pazienteId] && paiBuilderData[p.pazienteId])
    .map((p) => ({ ...patientDetails[p.pazienteId], timeline: p }));

  return (
    <AppLayout role="case-manager">
      <div className="space-y-6" data-testid="pai-hub-page">
        <div>
          <h1 className="text-2xl font-semibold text-foreground" data-testid="pai-hub-title">PAI Builder</h1>
          <p className="text-sm text-muted-foreground mt-1">Seleziona un paziente per aprire il Piano Assistenziale con supporto AI</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {patients.map((pt, i) => {
            const ai = paiBuilderData[pt.id]?.aiSuggestion;
            const initials = `${pt.cognome[0]}${pt.nome[0]}`;
            return (
              <motion.div
                key={pt.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Card
                  className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/case-manager/paziente/${pt.id}/pai-builder`)}
                  data-testid={`pai-hub-card-${pt.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-slate-500 dark:text-slate-300">{initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{pt.cognome} {pt.nome}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[9px]">{pt.cia}</Badge>
                          <Badge className="border-0 text-[9px] font-semibold" style={{ backgroundColor: `${pt.fragilitaAI.colore}15`, color: pt.fragilitaAI.colore }}>
                            {pt.tipoPAI}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2 line-clamp-1">{pt.diagnosi}</p>
                      </div>
                    </div>

                    {/* AI info strip */}
                    <div className="mt-4 pt-3 border-t border-border/30 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <Brain className="w-3 h-3 text-violet-400" />
                          <span className="text-[10px] font-semibold text-violet-600">AI {ai?.confidenza}%</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Activity className="w-3 h-3 text-muted-foreground" />
                          <span className="text-[10px] text-muted-foreground">{ai?.interventi?.length} interventi</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-muted-foreground" />
                          <span className="text-[10px] text-muted-foreground">{ai?.durata}</span>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
